import { CONSENT_EVENT_NAMES } from '@ui/lib/consent'

// SMS consent milestones fire from two surfaces — the onboarding consent
// switch (source 'onboarding') and the settings toggles (source 'comm_prefs')
// — so they are spelled once and spread into both blocks.
const SMS_OPT_IN_MILESTONES = {
  // "marketing" wire name intentionally differs from the announcements
  // feature key.
  smsAccountUpdatesEnabled: 'sms_account_updates_enabled',
  smsMarketingEnabled: 'sms_marketing_enabled',
} as const

// The names Branch receives from the browser: a copy of the server's
// branchEventWhitelist, one entry today. Goes away when Branch is backend-owned.
export const BRANCH_EVENTS: readonly string[] = ['registration_complete']

// Every analytics event name the web app emits lives here — renaming an
// event is an edit to this file, never to a component or emitter. Names are
// the wire vocabulary shared with the mobile apps (snake_case, dotted
// action.subject); the warehouse prefixes web rows with ue_w_ on ingest, so
// entries here match mobile rows by suffix.
export const ANALYTICS_EVENTS = {
  /** Published by EventTrackingContext.trackImpression, not a feature emitter. */
  impression: 'offer_view_impression',
  offers: {
    tap: 'button_tap.earn_more_offer',
    detailsView: 'screen_view.earn_more_offer',
    ctaClick: 'button_tap.earn_more_offer_cta',
    detailsClose: 'button_tap.exit_earn_more_offer',
  },
  cashAdvance: {
    // Taps match mobile names verbatim; `web-only` = minted here, the flow
    // step has no mobile screen equivalent.
    taps: {
      applicationIntroCta: 'button_tap.cash_advance_application_intro_cta',
      applicationBankUseExisting:
        'button_tap.cash_advance_application_existing_bank_use_existing',
      // Fired for the fresh-link and existing-bank states alike; the shared
      // Back control can't tell them apart.
      applicationBankExit: 'button_tap.cash_advance_application_bank_link_exit',
      applicationBankLinkCta:
        'button_tap.cash_advance_application_bank_link_cta',
      applicationBankLinkNew:
        'button_tap.cash_advance_application_existing_bank_link_new',
      // web-only
      applicationIdentityCta:
        'button_tap.cash_advance_application_identity_cta',
      // web-only
      applicationIdentityExit:
        'button_tap.cash_advance_application_identity_exit',
      applicationDebitUseExisting:
        'button_tap.cash_advance_application_existing_debit_use_existing',
      applicationDebitLinkNew:
        'button_tap.cash_advance_application_existing_debit_link_new',
      applicationDebitExit: 'button_tap.cash_advance_application_debit_exit',
      applicationReviewTermsToggle:
        'button_tap.cash_advance_application_review_terms_toggle',
      applicationReviewTermsLink:
        'button_tap.cash_advance_application_review_terms_link',
      applicationReviewCta: 'button_tap.cash_advance_application_review_cta',
      applicationReviewExit: 'button_tap.cash_advance_application_review_exit',
      homePromoTile: 'button_tap.home_cash_advance_tile',
      declinedRejectedReadMore:
        'button_tap.cash_advance_interim_application_rejected_cta',
      declinedIneligibleReadMore:
        'button_tap.cash_advance_interim_temporarily_ineligible_cta',
      takeOpen: 'button_tap.cash_advance_membership_request_cash_advance',
      requestTipPreset: 'button_tap.cash_advance_request_tip_preset_amount',
      requestAmountCta: 'button_tap.cash_advance_request_amount_cta',
      requestMembershipFeeToggle:
        'button_tap.cash_advance_request_membership_fee_terms_toggle',
      requestMembershipFeeTermsLink:
        'button_tap.cash_advance_request_membership_fee_terms_link',
      requestMembershipRefundToggle:
        'button_tap.cash_advance_request_membership_refund_terms_toggle',
      requestMembershipFeeCta:
        'button_tap.cash_advance_request_membership_fee_cta',
      requestConfirmationTermsToggle:
        'button_tap.cash_advance_request_confirmation_terms_toggle',
      requestConfirmationTermsLink:
        'button_tap.cash_advance_request_confirmation_terms_link',
      requestConfirmationCta:
        'button_tap.cash_advance_request_confirmation_cta',
      repayAdvance: 'button_tap.cash_advance_repay_advance',
      repayWithRewards: 'button_tap.cash_advance_repay_advance_with_rewards',
      // web-only
      repayWithCard: 'button_tap.cash_advance_repay_advance_with_card',
      // web-only
      repayLinkCard: 'button_tap.cash_advance_repay_link_card',
      repayConfirmationCta:
        'button_tap.cash_advance_repay_advance_confirmation',
    },
  },
  // Registration + sign-in. Names match the mobile apps verbatim.
  auth: {
    screens: {
      entryRoute: 'screen_view.entry_route',
      createAccount: 'screen_view.input_fields_registration',
      // Android's name; iOS reuses its MFA screen name here.
      loginPassword: 'screen_view.login_email_password',
      loginSmsCode: 'screen_view.login_sms_verification',
      verifyPhoneCode: 'screen_view.onboarding_sms_verification',
      // Mobile splits first-entry (OAuth) from change-number; we emit by case.
      phoneEntry: 'screen_view.onboarding_oauth_phone_input',
      phoneUpdate: 'screen_view.onboarding_phone_update',
      // Same screens reached from settings; mobile's settings names keep the
      // onboarding funnel clean.
      phoneUpdateReturning: 'screen_view.settings_personal_info_phone_update',
      verifyPhoneCodeReturning: 'screen_view.settings_phone_update_sms_verify',
      // iOS's name; Android's is sms-specific for the same surface.
      forgotPassword: 'screen_view.login_forgot_password',
      resetSent: 'screen_view.login_password_reset',
    },
    taps: {
      entryRouteNext: 'button_tap.entry_route_next',
      entryRouteBack: 'button_tap.entry_route_back',
      entryRouteGoogle: 'button_tap.entry_route_google_auth',
      entryRouteApple: 'button_tap.entry_route_apple_auth',
      createAccountNext: 'button_tap.input_fields_registration_next',
      createAccountBack: 'button_tap.input_fields_registration_back',
      loginPasswordNext: 'button_tap.login_email_password_next',
      loginPasswordBack: 'button_tap.login_email_password_back',
      loginForgotPassword: 'button_tap.login_email_forgot_password',
      loginSmsCodeNext: 'button_tap.login_sms_verification_next',
      loginSmsCodeBack: 'button_tap.login_sms_verification_back',
      loginSmsCodeResend: 'button_tap.login_sms_verification_resend_code',
      forgotPasswordNext: 'button_tap.login_sms_forgot_password_next',
      verifyPhoneCodeNext: 'button_tap.onboarding_sms_verification_next',
      verifyPhoneCodeResend:
        'button_tap.onboarding_sms_verification_resend_code',
      verifyPhoneCodeResendReturning: 'button_tap.settings_phone_verify_resend',
      verifyPhoneCodeBack: 'button_tap.onboarding_sms_verification_back',
      phoneEntryNext: 'button_tap.oauth_phone_next',
      phoneUpdateNext: 'button_tap.phone_update_next',
    },
    // Bare-noun funnel milestones, same family as mobile.
    milestones: {
      registered: 'registration',
      phoneVerified: 'verified_phone',
      // End of registration; the one name in BRANCH_EVENTS.
      registrationComplete: 'registration_complete',
      ...SMS_OPT_IN_MILESTONES,
    },
  },
  // Account settings hub + its destinations. Names match the mobile apps
  // verbatim; each destination adds its entries as it is built.
  account: {
    screens: {
      communicationPreferences: 'screen_view.communication_preferences',
      manageAccount: 'screen_view.settings_manage_account',
      manageMembership: 'screen_view.cash_advance_membership_management',
      personalInfo: 'screen_view.settings_personal_info',
      personalInfoAddress: 'screen_view.settings_personal_info_address',
      personalInfoEmailUpdate:
        'screen_view.settings_personal_info_email_update',
      personalInfoPhoneUpdate:
        'screen_view.settings_personal_info_phone_update',
      referrals: 'screen_view.referrals',
      referralInProgressInfo: 'screen_view.referral_in_progress_info',
      linkedAccounts: 'screen_view.linked_accounts',
      closureReason: 'screen_view.account_closure_reason',
      closureUnsettledAdvance: 'screen_view.account_closure_unsettled_advance',
      closureOverview: 'screen_view.account_closure_overview',
      closureConfirm: 'screen_view.account_closure_modal',
      // The keep-access offer, wherever it shows (mobile name).
      membershipPausePromo: 'screen_view.pause_membership_promo',
      // web-only: the apps dead-end a pause on an open advance; web hosts the
      // repay first.
      membershipPauseSettleAdvance:
        'screen_view.pause_membership_settle_advance',
      // Closure's "pause instead" framing (mobile name).
      closurePauseInstead:
        'screen_view.account_closure_pause_keep_access_modal',
      // web-only: the reason follow-up screens have no mobile names yet; the offers wall carries { reason }.
      closureOffers: 'screen_view.account_closure_offers',
      closureSmsPreferences: 'screen_view.account_closure_sms_preferences',
      // web-only: the settle step with only a fee to pay (no advance out).
      closureOutstandingFee: 'screen_view.account_closure_outstanding_fee',
    },
    taps: {
      commPreferences: 'button_tap.settings_comms_preferences',
      manageAccount: 'button_tap.settings_manage_account',
      manageMembership: 'button_tap.settings_manage_membership',
      needHelp: 'button_tap.settings_need_help',
      personalInfo: 'button_tap.settings_personal_info',
      referrals: 'button_tap.settings_referrals',
      referralsInviteFriend: 'button_tap.referrals_invite_friend',
      // web-only: desktop has no share sheet, so the code gets a copy button.
      referralsCopyCode: 'button_tap.referrals_copy_code',
      referralsHistoryInProgress:
        'button_tap.referrals_history_in_progress_referral',
      referralsHistoryCompleted:
        'button_tap.referrals_history_completed_referral',
      referralNotifyReferee:
        'button_tap.referral_in_progress_info_notify_referee',
      linkedAccounts: 'button_tap.settings_linked_bank_accounts',
      linkedAccountsUpdateBank: 'button_tap.linked_accounts_update_bank',
      linkedAccountsUpdateDebit: 'button_tap.linked_accounts_update_debit',
      linkedAccountsUpdateDebitExpiration:
        'button_tap.linked_accounts_update_debit_card_expiration',
      accountDeletion: 'button_tap.settings_account_deletion',
      // Carries { reason }.
      closureReasonSelect: 'button_tap.account_closure_reason_select',
      closureReasonBack: 'button_tap.account_closure_reason_back',
      closureUnsettledAdvanceCancel:
        'button_tap.account_closure_unsettled_advance_cancel',
      closureOverviewContinue:
        'button_tap.account_closure_overview_slide_continue',
      closureOverviewChangedMind:
        'button_tap.account_closure_overview_changed_mind',
      closureConfirm: 'button_tap.account_closure_modal_delete',
      closureCancel: 'button_tap.account_closure_modal_back',
      // Keep-access offer (mobile names): accept, and decline-into-pause.
      membershipPromoKeepAccess:
        'button_tap.pause_membership_promo_keep_access',
      membershipPromoPause:
        'button_tap.pause_membership_promo_pause_subscription',
      // Closure offer step (mobile names): pause & keep, and proceed to close.
      closurePauseKeepAccess: 'button_tap.account_closure_pause_keep_access',
      closureProceedClosing: 'button_tap.account_closure_pause_close_confirm',
      // web-only, no mobile equivalents yet.
      membershipPauseSettleAdvanceKeep:
        'button_tap.pause_membership_settle_advance_keep',
      closureReasonContactSupport:
        'button_tap.account_closure_reason_contact_support',
      closureOffersContinue: 'button_tap.account_closure_offers_continue',
      closureSmsPreferencesContinue:
        'button_tap.account_closure_sms_preferences_continue',
      closureSettleContinue: 'button_tap.account_closure_settle_continue',
    },
    // Emitted with { source: 'comm_prefs' } so the warehouse can tell the
    // settings toggles from the onboarding consent switch.
    milestones: {
      ...SMS_OPT_IN_MILESTONES,
      smsAccountUpdatesDisabled: 'sms_account_updates_disabled',
      smsMarketingDisabled: 'sms_marketing_disabled',
    },
  },
  // Post-verification wall (web-only screen). Legacy names kept for continuity.
  completeRegistration: {
    view: 'view_complete_registration',
    getApp: 'click_get_the_app',
    // web-only
    continueWeb: 'button_tap.complete_registration_continue_web',
  },
  // Dashboard get-the-app entry points (web-only).
  downloadApp: {
    sidebar: 'button_tap.sidebar_download_app',
    mobileHeader: 'button_tap.mobile_header_download_app',
  },
  // Shared banner, shared names: libs/ui/src/lib/consent/events.ts.
  consent: CONSENT_EVENT_NAMES,
} as const
